import type { ReactNode } from "react";
import { useSlideContext } from "./SlideContext";
import { SlideEyebrowTab } from "./SlideEyebrowTab";

interface SlideHeaderProps {
  eyebrow: string;
  children?: ReactNode;
  // When true, suppress the auto "NN _ " slide-number prefix — used by the
  // lettered appendix slides, which carry their own "Appendix X" label.
  unnumbered?: boolean;
  // Render the eyebrow as the corner tab (SlideEyebrowTab) instead of inline
  // text. The opt-in exists so a slide can take the deck's new eyebrow
  // treatment without also taking the five-row statement band.
  tab?: boolean;
}

// Standardized slide title block — pins eyebrow + title at the top of every
// slide so the header sits in the same place regardless of body content.
// The eyebrow is prefixed with a zero-padded slide number ("NN _ ") read from
// SlideContext so callers don't have to thread the index through every slide.
export function SlideHeader({
  eyebrow,
  children,
  unnumbered,
  tab,
}: SlideHeaderProps) {
  const ctx = useSlideContext();
  const prefix =
    !unnumbered && ctx != null
      ? `${(ctx.index + 1).toString().padStart(2, "0")} _ `
      : "";
  return (
    <header className="mb-8 flex flex-col md:mb-16">
      {tab ? (
        <SlideEyebrowTab eyebrow={eyebrow} unnumbered={unnumbered} />
      ) : (
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700 md:text-base md:tracking-[0.25em]">
          {prefix}
          {eyebrow}
        </p>
      )}
      {children ? <div className="mt-3 md:mt-5">{children}</div> : null}
    </header>
  );
}
